/**
 * TestingModePanelAdapter — Legacy's Testing Mode panel.
 *
 * Follows the Outbound pattern: we own all form state locally (rather than
 * using the shared TestingModePanel with renderExtraFields). This avoids the
 * DOM-observer hack needed to conditionally show the in-app role dropdown.
 *
 * The "In-App Role (Legacy Persona)" select appears whenever the Central role
 * is "member" — matching Central's own testing-mode UX and Outbound's pattern.
 */

import { useEffect, useState } from 'react';
import type { TestingModePanelProps } from '@levelup/ui-shell';
import type { ApplyOverrideParams, TestingModeOptions, TestingModeStatus } from '@levelup/central-auth';
import type { LegacyPersona } from '@legacy/shared';
import {
  applyTestingModeOverride,
  clearTestingModeOverride,
  fetchTestingModeOptions,
  fetchTestingModeStatus,
  setLegacyTestPersona,
} from '../lib/centralTestingMode';

const LEGACY_APP_ID = 'levelup-legacy';

// Legacy in-app personas that are distinct from Central roles.
// Owner / Admin / Viewer are covered by the Central "Role" dropdown above,
// so we only list the app-specific member personas here.
const LEGACY_PERSONAS: LegacyPersona[] = [
  'Legacy Operator',
  'Legacy Analyst',
  'Legacy Contributor',
];

type WorkspaceOption = TestingModeOptions['workspaces'][number];
type AppOption = TestingModeOptions['apps'][number];
type RoleOption = TestingModeOptions['roles'][number];
type CapabilityPresetOption = TestingModeOptions['capabilityPresets'][number];
type VisibilityPresetOption = TestingModeOptions['visibilityPresets'][number];
type MemberTypeOption = TestingModeOptions['memberTypes'][number];

export function TestingModePanelAdapter({ isOpen, onClose }: TestingModePanelProps) {
  const [options, setOptions] = useState<TestingModeOptions | null>(null);
  const [status, setStatus] = useState<TestingModeStatus | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState('');

  const [workspaceId, setWorkspaceId] = useState('');
  const [appId, setAppId] = useState('');
  const [role, setRole] = useState('');
  const [capabilityPreset, setCapabilityPreset] = useState('');
  const [visibilityPreset, setVisibilityPreset] = useState('');
  const [memberType, setMemberType] = useState('');
  const [legacyPersona, setLegacyPersona] = useState<LegacyPersona>(LEGACY_PERSONAS[0]);

  // Load options + status when panel opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setOptionsLoading(true);
    setError('');
    Promise.all([fetchTestingModeOptions(), fetchTestingModeStatus()])
      .then(([opts, st]) => {
        if (cancelled) return;
        setOptions(opts);
        setStatus(st);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load testing mode');
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Pre-fill form from active override
  useEffect(() => {
    if (status?.active && status.override) {
      const o = status.override;
      setWorkspaceId(o.workspaceId || '');
      setAppId(o.appId || '');
      setRole(o.role || '');
      setCapabilityPreset(o.capabilityPreset || '');
      setVisibilityPreset(o.visibilityPreset || '');
      setMemberType(o.memberType || '');
    }
  }, [status]);

  if (!isOpen) return null;

  const canApply = workspaceId && appId && role && !isApplying;
  const showPersona = role === 'member';

  const handleApply = async () => {
    setError('');
    setIsApplying(true);
    const params: ApplyOverrideParams = { workspaceId, appId, role };
    if (capabilityPreset) params.capabilityPreset = capabilityPreset;
    if (visibilityPreset) params.visibilityPreset = visibilityPreset;
    if (memberType) params.memberType = memberType;

    // Attach the Legacy persona as a custom scope only when the override
    // actually applies to Legacy. Other apps are not affected.
    if (appId === LEGACY_APP_ID && role === 'member' && legacyPersona) {
      params.customScopes = [`legacy.persona:${legacyPersona}`];
    }

    try {
      // Persist the in-app persona locally so usePermissions/useGetMenuQuery
      // can apply persona-aware gating after the reload. We only check the
      // Central role here — the appId may differ across deployments (e.g.
      // "legacy" vs "levelup-legacy"), so we store whenever the tester
      // explicitly picked a persona for a member-level override.
      setLegacyTestPersona(role === 'member' ? legacyPersona : null);
      await applyTestingModeOverride(params);
      onClose();
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply override');
      setIsApplying(false);
    }
  };

  const handleClear = async () => {
    setError('');
    setIsClearing(true);
    try {
      setLegacyTestPersona(null);
      await clearTestingModeOverride();
      onClose();
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear override');
      setIsClearing(false);
    }
  };

  return (
    <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Testing Mode</h3>
        {status?.active && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            Active
          </span>
        )}
      </div>

      {optionsLoading ? (
        <p className="text-sm text-gray-500">Loading options...</p>
      ) : !options ? (
        <p className="text-sm text-red-600">Failed to load testing mode options.</p>
      ) : (
        <>
          <Field label="Workspace *">
            <select
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-legacy-500"
            >
              <option value="">Select workspace...</option>
              {options.workspaces.map((ws: WorkspaceOption) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="App *">
            <select
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-legacy-500"
            >
              <option value="">Select app...</option>
              {options.apps.map((app: AppOption) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Role *">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-legacy-500"
            >
              <option value="">Select role...</option>
              {options.roles.map((r: RoleOption) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>

          {options.capabilityPresets.length > 0 && (
            <Field label="Capability Preset">
              <select
                value={capabilityPreset}
                onChange={(e) => setCapabilityPreset(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-legacy-500"
              >
                <option value="">None</option>
                {options.capabilityPresets.map((p: CapabilityPresetOption) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {options.visibilityPresets.length > 0 && (
            <Field label="Visibility Preset">
              <select
                value={visibilityPreset}
                onChange={(e) => setVisibilityPreset(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-legacy-500"
              >
                <option value="">None</option>
                {options.visibilityPresets.map((p: VisibilityPresetOption) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {options.memberTypes.length > 0 && (
            <Field label="Member Type">
              <select
                value={memberType}
                onChange={(e) => setMemberType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-legacy-500"
              >
                <option value="">None</option>
                {options.memberTypes.map((mt: MemberTypeOption) => (
                  <option key={mt.key} value={mt.key}>
                    {mt.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {showPersona && (
            <Field label="In-App Role (Legacy Persona)">
              <select
                value={legacyPersona}
                onChange={(e) => setLegacyPersona(e.target.value as LegacyPersona)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-legacy-500"
              >
                {LEGACY_PERSONAS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-gray-500">
                Applied only when App is Legacy. Informational — Legacy enforces authorization via capabilities.
              </p>
            </Field>
          )}
        </>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button
          onClick={handleApply}
          disabled={!canApply}
          className="flex-1 rounded-md bg-legacy-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-legacy-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isApplying ? 'Applying...' : 'Apply Override'}
        </button>
        {status?.active && (
          <button
            onClick={handleClear}
            disabled={isClearing}
            className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-200 disabled:opacity-50"
          >
            {isClearing ? 'Clearing...' : 'Clear'}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
